import type { OperationsApartmentStay, OperationsApartmentTurnover } from '@/shared/api';

interface OperationsPresentation {
  label: string;
  badgeClassName: string;
  edgeClassName: string;
  rowClassName: string;
  iconClassName: string;
}

const neutral: OperationsPresentation = {
  label: 'Unbekannter Status',
  badgeClassName: 'border-line bg-paper text-ink-soft',
  edgeClassName: 'border-l-line',
  rowClassName: 'hover:bg-card-alt',
  iconClassName: 'bg-paper text-ink-soft',
};

const sage = style(
  'bg-sage/10 text-sage border-sage/30',
  'border-l-sage',
  'hover:bg-sage/10',
  'bg-sage/10 text-sage',
);
const sealwax = style(
  'bg-sealwax/10 text-sealwax border-sealwax/30',
  'border-l-sealwax',
  'hover:bg-sealwax/10',
  'bg-sealwax/10 text-sealwax',
);
const amber = style(
  'bg-amber/20 text-ink border-amber/40',
  'border-l-amber',
  'hover:bg-amber/10',
  'bg-amber/20 text-ink',
);

const stayPhases: Record<string, OperationsPresentation> = {
  Arrival: {
    ...amber,
    label: 'Anreise',
    edgeClassName: 'border-t-amber',
    iconClassName: 'bg-amber/10 text-amber',
  },
  Departure: { ...sealwax, label: 'Abreise' },
  InHouse: { ...sage, label: 'Im Haus' },
  Upcoming: { ...amber, label: 'Anstehend' },
  Pending: { ...amber, label: 'Ausstehend' },
  Mixed: { ...amber, label: 'Gemischt' },
  Completed: { ...neutral, label: 'Abgeschlossen' },
  Cancelled: { ...neutral, label: 'Storniert' },
  NoShow: { ...sealwax, label: 'Nicht angereist' },
  DataQuality: { ...sealwax, label: 'Unklare Buchungsdaten' },
};

const cleaningStates: Record<string, OperationsPresentation> = {
  Issue: { ...sealwax, label: 'Problem' },
  Ready: { ...sage, label: 'Bereit' },
  InProgress: { ...amber, label: 'In Arbeit' },
  Pending: { ...sealwax, label: 'Noch offen' },
  Cancelled: { ...neutral, label: 'Entfällt' },
};

export function stayPhasePresentation(value: string): OperationsPresentation {
  return stayPhases[value] ?? neutral;
}

export function cleaningStatePresentation(value: string): OperationsPresentation {
  return cleaningStates[value] ?? neutral;
}

export function cleaningTaskPresentation(
  task: OperationsApartmentTurnover['cleaningTasks'][number],
) {
  if (task.hasOpenIssue || task.state === 'Issue') return { ...sealwax, label: 'Problem' };
  if (task.isOverdue) return { ...sealwax, label: 'Überfällig' };
  return cleaningStatePresentation(task.state);
}

export function cleaningTaskToneClass(task: OperationsApartmentTurnover['cleaningTasks'][number]) {
  if (task.hasOpenIssue || task.isOverdue || task.state === 'Issue' || task.state === 'Pending')
    return 'text-sealwax';
  if (task.state === 'InProgress') return 'text-amber';
  if (task.state === 'Ready') return 'text-sage';
  return 'text-ink-faint';
}

export function occupancyLabel(
  stay: Pick<
    OperationsApartmentStay,
    'guestCount' | 'adults' | 'children' | 'infants' | 'occupancyDataQualityCode'
  >,
): string {
  if (stay.occupancyDataQualityCode !== 'Verified') {
    return `${stay.guestCount} Gäste · Aufteilung nicht verifiziert`;
  }

  const parts = [`${stay.adults} Erw.`];
  if (stay.children) parts.push(`${stay.children} ${stay.children === 1 ? 'Kind' : 'Kinder'}`);
  if (stay.infants) {
    parts.push(`${stay.infants} ${stay.infants === 1 ? 'Kleinkind' : 'Kleinkinder'}`);
  }
  return parts.join(' · ');
}

export function orderedCleaningTasks(apartment: OperationsApartmentTurnover) {
  return [...apartment.cleaningTasks].sort(
    (left, right) =>
      cleaningPriority(left) - cleaningPriority(right) || left.id.localeCompare(right.id),
  );
}

function cleaningPriority(task: OperationsApartmentTurnover['cleaningTasks'][number]) {
  if (task.hasOpenIssue || task.state === 'Issue') return 0;
  if (task.isOverdue) return 1;
  if (task.state === 'InProgress') return 2;
  if (task.state === 'Pending') return 3;
  if (task.state === 'Ready') return 4;
  return 5;
}

function style(
  badgeClassName: string,
  edgeClassName: string,
  rowClassName: string,
  iconClassName: string,
): OperationsPresentation {
  return { label: '', badgeClassName, edgeClassName, rowClassName, iconClassName };
}
